import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useDashboardController from '../controllers/useDashboardController';

function Dashboard() {
  const { 
    username,
    title,
    bio,
    cvs,
    loading,
    error,
    message,
    createWebPage,
    selectedCvId
  } = useDashboardController();

  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [tempUsername, setTempUsername] = useState(username);
  const [tempTitle, setTempTitle] = useState(title);
  const [tempBio, setTempBio] = useState(bio);
  const [tempSelectedCvId, setTempSelectedCvId] = useState(null);

  useEffect(() => {
    setTempUsername(username);
    setTempTitle(title);
    setTempBio(bio);
    setTempSelectedCvId(selectedCvId);
  }, [username, title, bio, selectedCvId]);

  const handleCreateOrUpdate = async () => {
    try {
      const success = await createWebPage(tempUsername, tempTitle, tempBio, tempSelectedCvId);
      if (success) {
        setIsModalOpen(false);
        setEditMode(false);
      }
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleWebPageClick = () => {
    if (!username) {
      setIsModalOpen(true);
      setEditMode(false);
    }
  };

  return (
    <section className="flex-1 py-5 px-5 bg-gray-100">
      {/* Header Section */}
      <div className='my-12'>
        <h1 className="text-5xl font-extrabold text-center lg:text-7xl 2xl:text-8xl">
          <span className="text-transparent bg-gradient-to-br bg-clip-text from-sky-800 via-sky-600 to-sky-700">
            Welcome to Portoize
          </span>
        </h1>

        <p className="max-w-3xl mx-auto mt-6 text-lg text-center text-gray-700 dark:text-white md:text-xl">
          Create your personal web page and multiple CVs for personal branding.
        </p>
      </div>

      {/* Action Cards */}
      <div className='flex flex-wrap md:mx-10 md:my-10 my-5 justify-center gap-5'>
        <div className="flex max-w-md overflow-hidden cursor-pointer bg-white rounded-lg shadow-lg hover:shadow-2xl dark:bg-gray-800">
          <div className="p-4 md:p-4">
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">Generate CV with AI</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Coming Soon</p>
          </div>
        </div>

        <div onClick={() => window.open('/editor', '_blank')} className="flex max-w-md overflow-hidden cursor-pointer bg-white rounded-lg shadow-lg hover:shadow-2xl dark:bg-gray-800">
          <div className="p-4 md:p-4">
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">Create CV with Editor</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Create Your CV on Editor Based On Your Data</p>
          </div>
        </div>

        <div 
          onClick={handleWebPageClick}
          className={`flex max-w-md overflow-hidden cursor-pointer bg-white rounded-lg shadow-lg ${
            !username ? 'hover:shadow-2xl' : 'opacity-75 cursor-not-allowed'
          } dark:bg-gray-800`}
        >
          <div className="p-4 md:p-4">
            <h1 className="text-xl font-bold text-gray-800 dark:text-white">
              {username ? 'My Web Page' : 'Create Web Page'}
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              {username 
                ? 'Your personal web page is already created' 
                : 'Build Your Personal Web Page For Your Personal Branding'}
            </p>
            {username && (
              <div className="flex items-center justify-end mt-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditMode(true);
                    setIsModalOpen(true);
                  }}
                  className="p-1 text-blue-500 hover:text-blue-600"
                  title="Edit Web Page"
                >
                  ✏️ Edit
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CV List & Web Page Status Section */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
        {/* CV List Section */}
        <div className="container px-6 py-10 mx-auto">
          <h1 className="text-2xl font-semibold text-gray-800 capitalize lg:text-3xl dark:text-white">My CV</h1>
          { error && <p className="text-center text-red-500">{error}</p> }
          { message && <p className="text-center text-gray-500">{message}</p> }
          
          { !loading && !error && !message && ( 
            <div className="grid grid-cols-1 mt-1 md:mt-2">
              {cvs.map((cv) => (
                <div 
                  key={cv.id} 
                  onClick={() => window.open(`/editor/${cv.id}`, '_blank')} 
                  className="flex my-3 max-w-md overflow-hidden bg-white rounded-lg shadow-lg cursor-pointer hover:shadow-2xl"
                >
                  <div className="p-4 md:p-4">
                    <h1 className="text-xl font-bold text-gray-800 dark:text-white">
                      {`CV For ${cv.cv_data.profile.title}`}
                    </h1>
                    <p className="text-gray-600">
                      Created at: {new Date(cv.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Web Page Status Section */}
        <div className="container px-6 py-10 mx-auto">
          <h1 className="text-2xl font-semibold text-gray-800 capitalize lg:text-3xl dark:text-white">
            Web Page Status
          </h1>
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          {message && <p className="text-green-500 text-sm mb-4">{message}</p>}
          
          <div className="grid grid-cols-1 mt-2 md:mt-4 gap-8">
            <div className="flex flex-col max-w-md bg-white rounded-lg shadow-lg dark:bg-gray-800">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
                onClick={(e) => {
                  e.preventDefault();
                  if (username) {
                    window.open(`/u/${username}`, '_blank');
                  }
                }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{title || 'No title set'}</h3>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                      {username || 'No web page created yet'}
                    </p>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400 line-clamp-3">
                      {bio || 'No bio provided'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal untuk create/edit web page */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div 
            className="bg-white p-6 rounded-lg w-96 dark:bg-gray-800"
            onClick={(e) => e.stopPropagation()}
          >
            <h1 className="text-2xl font-semibold mb-4 dark:text-white">
              {editMode ? 'Edit Web Page' : 'Create Web Page'}
            </h1>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Username
                </label>
                <input
                  type="text"
                  value={tempUsername}
                  onChange={(e) => setTempUsername(e.target.value)}
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="unique_username"
                />
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                  Only letters, numbers, and underscores allowed
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Title
                </label>
                <input
                  type="text"
                  value={tempTitle}
                  onChange={(e) => setTempTitle(e.target.value)}
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="e.g. Software Developer | UI/UX Designer"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Bio
                </label>
                <textarea
                  value={tempBio}
                  onChange={(e) => setTempBio(e.target.value)}
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Describe yourself..."
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Choose CV
                </label>
                <select
                  value={tempSelectedCvId || ''}
                  onChange={(e) => setTempSelectedCvId(Number(e.target.value))}
                  className="mt-1 p-2 w-full border border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Choose CV</option>
                  {cvs.map((cv) => (
                    <option key={cv.id} value={cv.id}>
                      {cv.cv_data.profile.title} - {new Date(cv.created_at).toLocaleDateString()}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={handleCreateOrUpdate}
                  disabled={loading}
                  className={`flex-1 px-4 py-2 rounded-md ${
                    loading ? 'bg-gray-400' : 'bg-blue-500 hover:bg-blue-600'
                  } text-white transition-colors`}
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
                
                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setEditMode(false);
                  }}
                  className="px-4 py-2 rounded-md bg-gray-500 hover:bg-gray-600 text-white transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default Dashboard;